import { Calendar, Check, UserPlus, LayoutList } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Badge } from '@/components/common/Badge'
import { cn } from '@/lib/utils'
import type { Task, TaskPriority } from '@/types'

interface TaskCardProps {
  task: Task
  onClick?: ((e: React.MouseEvent) => void) | undefined
  dragging?: boolean | undefined
  selected?: boolean | undefined
  selectionMode?: boolean | undefined
  onAssignClick?: ((taskId: string, e: React.MouseEvent) => void) | undefined
}

const PRIORITY_BORDER: Record<TaskPriority, string> = {
  LOW: 'border-l-slate-300',
  MEDIUM: 'border-l-accent-400',
  HIGH: 'border-l-warning-500',
  URGENT: 'border-l-danger-500',
}

const PRIORITY_BADGE: Record<TaskPriority, 'neutral' | 'info' | 'warning' | 'danger'> = {
  LOW: 'neutral',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'danger',
}

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  LOW: 'Bassa',
  MEDIUM: 'Media',
  HIGH: 'Alta',
  URGENT: 'Urgente',
}

export function TaskCard({ task, onClick, dragging = false, selected = false, selectionMode = false, onAssignClick }: TaskCardProps) {
  const navigate = useNavigate()
  const now = new Date()
  const dueDate = task.dueDate ? new Date(task.dueDate) : null
  const isOverdue =
    dueDate &&
    dueDate < now &&
    task.status !== 'DONE' &&
    task.status !== 'CANCELLED'
  const isDueSoon =
    dueDate &&
    !isOverdue &&
    dueDate.getTime() - now.getTime() < 24 * 60 * 60 * 1000 &&
    task.status !== 'DONE' &&
    task.status !== 'CANCELLED'

  return (
    <div
      className={cn(
        'bg-white rounded-md border border-slate-200 p-3 relative',
        'border-l-2 transition-all',
        isOverdue ? 'border-l-danger-600 bg-danger-50/30' : isDueSoon ? 'border-l-warning-500 bg-warning-50/20' : PRIORITY_BORDER[task.priority],
        dragging ? 'shadow-lg opacity-80' : 'shadow-card hover:shadow-card-hover',
        onClick && 'cursor-pointer',
        selected && 'ring-2 ring-accent-500 bg-accent-50/40'
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(e as unknown as React.MouseEvent) } : undefined}
    >
      {/* Selection checkbox */}
      {selectionMode && (
        <div className={cn(
          'absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full border-2 flex items-center justify-center z-10 transition-colors',
          selected
            ? 'bg-accent-600 border-accent-600'
            : 'bg-white border-slate-300'
        )}>
          {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
        </div>
      )}

      {/* Client & Project label */}
      {(task.client || (task as any).project) && (
        <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1 truncate">
          {task.client?.name}{task.client && (task as any).project?.name ? ' — ' : ''}{(task as any).project?.name}
        </p>
      )}

      {/* Department badge when unassigned */}
      {!task.assignee && (task as any).department && (
        <div className="mb-1.5">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-50 text-purple-700 border border-purple-200">
            {(task as any).department.name}
          </span>
        </div>
      )}

      {/* Title */}
      <p className="text-sm font-medium text-slate-800 leading-snug mb-2 line-clamp-2">
        {task.title}
      </p>

      {/* Footer: priority + assignee + due date */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Badge variant={PRIORITY_BADGE[task.priority]} size="sm">
            {PRIORITY_LABEL[task.priority]}
          </Badge>
          {task.content?.editorialPlan?.projectId && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/ped?projectId=${task.content!.editorialPlan!.projectId}&contentId=${task.content!.id}`)
              }}
              title="Vai al piano editoriale"
              className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 transition-colors"
            >
              <LayoutList className="w-2.5 h-2.5" />
              PED
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {task.dueDate && (
            <div
              className={cn(
                'flex items-center gap-1 text-[11px]',
                isOverdue ? 'text-danger-600 font-semibold' : isDueSoon ? 'text-warning-600 font-medium' : 'text-slate-400'
              )}
            >
              <Calendar className="w-3 h-3" />
              {new Date(task.dueDate).toLocaleDateString('it-IT', {
                day: '2-digit',
                month: 'short',
              })}
            </div>
          )}
          {/* Assignee — clickable for quick-assign */}
          <div
            onClick={(e) => {
              if (onAssignClick) {
                e.stopPropagation()
                onAssignClick(task.id, e)
              }
            }}
            className={cn(
              'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0',
              onAssignClick && 'cursor-pointer',
              task.assignee
                ? 'bg-accent-600'
                : onAssignClick
                ? 'border border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 transition-colors'
                : ''
            )}
            title={task.assignee ? `${task.assignee.firstName} ${task.assignee.lastName} — clicca per riassegnare` : 'Assegna'}
          >
            {task.assignee ? (
              <span className="text-[9px] font-semibold text-white">
                {task.assignee.firstName.charAt(0)}
              </span>
            ) : onAssignClick ? (
              <UserPlus className="w-2.5 h-2.5 text-slate-400" />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
